import { useEffect, useState } from 'react';
import { useField } from 'formik';
import { Space } from 'antd';
import FileTextIconOutlined from 'components/icons/FileTextIconOutlined';
import FormSectionHeader from 'components/new-ui/base/form/FormSectionHeader';
import ReadonlyFormItem from 'components/new-ui/base/form/ReadonlyFormItem';
import SearchBar from 'components/new-ui/base/searchbar/SearchBar';
import getDeclarationsOptions from 'components/ui/base/searchbar/search-options/DeclarationsOptions';
import { useMultiStepFormContext } from 'components/new-ui/composed/multistep-form/MultiStepFormProvider';
import useGlobalOverlay from 'hooks/useGlobalOverlay';
import { Declaration } from 'store/declarations/declaration';
import { listDeclarations } from 'store/declarations/client';
import { DeclarationInternalType } from 'store/declarations/enums/common/declaration-internal-type';
import { DeclarationExternalEntity } from 'store/declarations/enums/common/declaration-external-entity';
import { debounce } from 'lodash';
import { BrokerUploadSteps } from '../types';

const stepKey = BrokerUploadSteps.DECLARATION;

const DeclarationStep = () => {
    const [declarationIdField, , declarationIdHelpers] = useField('declaration.declarationId');
    const [, , mrnHelpers] = useField('declaration.mrn');
    const { isActive, isComplete, isIdle, resetStep } = useMultiStepFormContext<any>();
    const { showGlobalOverlay, hideGlobalOverlay } = useGlobalOverlay();
    const [declarations, setDeclarations] = useState<Declaration[]>([]);

    const fetchDeclarations = (query?: string) =>
        listDeclarations({
            query: query ? encodeURIComponent(query) : undefined,
            size: 200,
            hasMrn: true,
            declarationInternalType: [DeclarationInternalType.EXPORT],
            declarationExternalEntity: DeclarationExternalEntity.REVENUE,
        })
            .then((data) => setDeclarations(data.list))
            .finally(() => hideGlobalOverlay());

    useEffect(() => {
        if (!isActive(stepKey)) return;
        showGlobalOverlay({ type: 'LoadingOverlay', payload: { message: 'Loading declarations...' } });
        fetchDeclarations();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isActive(stepKey)]);

    const debouncedSearch = debounce((query: string) => fetchDeclarations(query), 700);

    const sectionTitle = 'Declaration';

    if (isIdle(stepKey)) {
        return <FormSectionHeader title={sectionTitle} icon={<FileTextIconOutlined />} />;
    }

    if (isComplete(stepKey)) {
        return (
            <div style={{ display: 'flex', alignItems: 'center' }}>
                <FormSectionHeader title={sectionTitle} icon={<FileTextIconOutlined />} />
                <ReadonlyFormItem
                    disabled={false}
                    value={declarationIdField.value}
                    onReset={() => resetStep(stepKey, 'declarationId')}
                />
            </div>
        );
    }

    if (isActive(stepKey)) {
        return (
            <Space style={{ width: '100%' }} direction="vertical">
                <FormSectionHeader title={sectionTitle} icon={<FileTextIconOutlined />} />
                <SearchBar
                    style={{ width: '100%' }}
                    inputPlaceholder="Search Declaration"
                    options={getDeclarationsOptions(declarations, (d: Declaration) => [d.id, d.mrn])}
                    onSearch={(q) => debouncedSearch(q)}
                    onSelect={(value: [string, string]) => {
                        declarationIdHelpers.setValue(value[0]);
                        mrnHelpers.setValue(value[1]);
                    }}
                />
            </Space>
        );
    }

    return null;
};

export default DeclarationStep;
